// Global Instructions Rule Applied!
//
// useEmailRecipients — Phase 3 recipients-tab data layer for the email
// compose form.
//
// Mirrors useSendCommunication (the Phase 5 distribution-lists send screen)
// in structure: screen-local state, no Redux pre-fetch (Phase 2 precedent
// for module-local data — see useEmailCampaigns). Reuses the
// distribution-lists endpoints verbatim for the saved-list picker and the
// filter-options universe — those are shared resources, not duplicated.
//
// Lifecycle from the consumer's perspective:
//   1. Mount: fetch savedLists + filter-options + (if a draft already has
//      staged recipients) the current staged-recipients grid.
//   2. User picks a saved list / amends filters → "Resolve" → resolve() →
//      sets `resolved` to the bare array (session-only; never persists).
//   3. User toggles per-recipient exclusion / role → "Stage" → stage() →
//      calls stage_email_recipients RPC via the backend → on success,
//      refetches the staged grid via view__email_recipients.
//   4. User edits one row's override → savePerRecipientOverride() →
//      refetches the row's slice of the grid.

import { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import type {
  DistributionListGridModel,
  DistributionListFilterOptionsModel
} from '../../../state_manager/data_models/distribution_list_grid_model.ts'
import type { DistributionListRecipient } from '../../../state_manager/data_models/distribution_list_recipients_model.ts'
import type {
  EmailRecipientGridModel,
  EmailRecipientStagePayload,
  EmailRecipientStageItem,
  EmailRecipientOverridePayload
} from '../../../state_manager/data_models/email_recipient_grid_model.ts'
import type {
  EmailResolveRecipientsPayload,
  FilterValue,
  SortConfig
} from '../../../state_manager/interfaces/email.ts'
import {
  fetchDistributionListsFiltered,
  fetchDistributionListFilterOptions,
  fetchDistributionListDetail
} from '../../distribution_lists/services/distributionListsService.ts'
import {
  resolveEmailRecipients,
  fetchEmailRecipientsFiltered,
  stageEmailRecipients,
  saveEmailRecipientOverride,
  // Phase 4b — team-mode mutators
  saveTeamOverride,
  addTeamRecipient,
  removeRecipient,
  searchTeamCandidates,
  type TeamCandidateContact
} from '../services/emailService.ts'

export const DEFAULT_RECIPIENTS_SORTING: SortConfig[] = [
  { key: 'email_recipients__account_name', direction: 'asc' }
]

export interface UseEmailRecipientsOptions {
  /** The campaign whose recipients this hook manages. Null = compose form
   *  is in 'new' mode (no draft persisted yet); the hook stays inert. */
  campaignId: string | null
}

export const useEmailRecipients = ({ campaignId }: UseEmailRecipientsOptions) => {
  // ── Sources for the Pick-recipients drawer (saved lists + option universes)
  const [savedLists, setSavedLists] = useState<DistributionListGridModel[]>([])
  const [listsLoading, setListsLoading] = useState(false)
  const [options, setOptions] = useState<DistributionListFilterOptionsModel | null>(null)
  const [optionsLoading, setOptionsLoading] = useState(false)

  // ── Session-only resolve buffer (analogous to useSendCommunication's
  //    `recipients` — never persisted until the user clicks Stage).
  const [resolved, setResolved] = useState<DistributionListRecipient[] | null>(null)
  const [resolving, setResolving] = useState(false)

  // ── Persisted staged grid (from view__email_recipients).
  const [staged, setStaged] = useState<EmailRecipientGridModel[]>([])
  const [stagedLoading, setStagedLoading] = useState(false)
  const [stagedTotal, setStagedTotal] = useState(0)
  const [staging, setStaging] = useState(false)

  const cancelledRef = useRef(false)
  useEffect(() => {
    cancelledRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [])

  // Saved lists + filter-options are constant per session (refresh on
  // mount). Both are reads from existing distribution-lists endpoints
  // (CLAUDE.md frontend rule: reuse the same components / endpoints).
  useEffect(() => {
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      if (!token) return
      setListsLoading(true)
      setOptionsLoading(true)
      const [listsRes, optionsRes] = await Promise.all([
        fetchDistributionListsFiltered(
          [{ key: 'distribution_lists__name', direction: 'asc' }],
          [],
          1,
          null,
          token
        ),
        fetchDistributionListFilterOptions(token)
      ])
      if (cancelledRef.current) return
      setSavedLists(listsRes.data)
      setListsLoading(false)
      setOptions(optionsRes.data ?? null)
      setOptionsLoading(false)
    })()
  }, [])

  // Cold-load the staged-recipients grid whenever the campaignId changes.
  // This is the CLAUDE.md hard-refresh-form path: the form URL is enough
  // to repopulate the recipients tab via the data RPC.
  const fetchStaged = useCallback(
    async (sorting: SortConfig[] = DEFAULT_RECIPIENTS_SORTING, filters: FilterValue[] = []) => {
      if (!campaignId) {
        setStaged([])
        setStagedTotal(0)
        return
      }
      setStagedLoading(true)
      const token = getAccessToken(store.getState().auth)
      const res = await fetchEmailRecipientsFiltered(
        campaignId,
        sorting.length > 0 ? sorting : DEFAULT_RECIPIENTS_SORTING,
        filters,
        1,
        null,
        token
      )
      if (cancelledRef.current) return
      if (res.error && process.env.NODE_ENV === 'development') console.warn(res.error)
      setStaged(res.data)
      setStagedTotal(res.totalCount)
      setStagedLoading(false)
    },
    [campaignId]
  )

  useEffect(() => {
    if (campaignId) fetchStaged()
  }, [campaignId, fetchStaged])

  /** Cold-load one saved list's full definition (reuses Phase 4 detail RPC).
   *  Returns the row so the drawer can pre-populate the editable filters AND
   *  seed accounts_add ← account_ids / contacts_add ← contact_ids
   *  (distribution-lists §2.13b). */
  const loadListDefinition = useCallback(
    async (listId: string): Promise<DistributionListGridModel | null> => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchDistributionListDetail(listId, token)
      if (res.error) return null
      return res.data ?? null
    },
    []
  )

  /** Resolve a (possibly amended) distribution-list definition under the
   *  email module's URL. Identical wire shape to the distribution-lists
   *  resolve; same backend resolver. Sets `resolved` on success. */
  const resolve = useCallback(
    async (payload: EmailResolveRecipientsPayload): Promise<string | null> => {
      if (!campaignId) return 'Save the draft before resolving recipients.'
      setResolving(true)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await resolveEmailRecipients(campaignId, payload, token)
        if (cancelledRef.current) return null
        if (res.error) {
          setResolved([])
          return res.error
        }
        setResolved(res.data)
        return null
      } finally {
        if (!cancelledRef.current) setResolving(false)
      }
    },
    [campaignId]
  )

  const clearResolved = useCallback(() => setResolved(null), [])

  /** Stage the given recipients into the campaign. The hook owns the
   *  recipient_count snapshot returned by the RPC; after a successful
   *  stage we refetch the grid so the Recipients tab shows the canonical
   *  persisted set. */
  const stage = useCallback(
    async (
      recipients: EmailRecipientStageItem[],
      resolutionParams: Record<string, unknown> = {}
    ): Promise<string | null> => {
      if (!campaignId) return 'Save the draft before staging recipients.'
      setStaging(true)
      try {
        const token = getAccessToken(store.getState().auth)
        const payload: EmailRecipientStagePayload = {
          recipients,
          resolution_params: resolutionParams
        }
        const res = await stageEmailRecipients(campaignId, payload, token)
        if (cancelledRef.current) return null
        if (res.error) return res.error
        // Refetch the staged grid (the backend authoritatively
        // returns the new row set including server-assigned ids).
        await fetchStaged()
        // After a successful stage the session-only resolve buffer is
        // no longer relevant — the staged grid is the source of truth.
        setResolved(null)
        return null
      } finally {
        if (!cancelledRef.current) setStaging(false)
      }
    },
    [campaignId, fetchStaged]
  )

  /** Save one recipient's override (subject / body / role). Refetches
   *  the staged grid so the row's has_*_override flags update. */
  const savePerRecipientOverride = useCallback(
    async (
      recipientId: string,
      payload: EmailRecipientOverridePayload
    ): Promise<string | null> => {
      const token = getAccessToken(store.getState().auth)
      const res = await saveEmailRecipientOverride(recipientId, payload, token)
      if (cancelledRef.current) return null
      if (res.error) return res.error
      await fetchStaged()
      return null
    },
    [fetchStaged]
  )

  // ── Phase 4b: team-mode mutations ──────────────────────────────────────

  /** Save a per-team subject / body override (Phase 4b). Backend writes the
   *  same values to every non-trashed row of (campaign, account). After
   *  success we refetch so every row's has_*_override flag updates. */
  const savePerTeamOverride = useCallback(
    async (
      accountId: string,
      payload: { subject_override?: string | null; body_override_html?: string | null }
    ): Promise<string | null> => {
      if (!campaignId) return 'Save the draft before editing the team override.'
      const token = getAccessToken(store.getState().auth)
      const res = await saveTeamOverride(campaignId, accountId, payload, token)
      if (cancelledRef.current) return null
      if (res.error) return res.error
      await fetchStaged()
      return null
    },
    [campaignId, fetchStaged]
  )

  /** Add one contact (already on the account) to a team (Phase 4b). */
  const addRecipientToTeam = useCallback(
    async (
      accountId: string,
      payload: { contact_id: string; role: 'to' | 'cc' }
    ): Promise<string | null> => {
      if (!campaignId) return 'Save the draft before adding recipients.'
      const token = getAccessToken(store.getState().auth)
      const res = await addTeamRecipient(campaignId, accountId, payload, token)
      if (cancelledRef.current) return null
      if (res.error) return res.error
      await fetchStaged()
      return null
    },
    [campaignId, fetchStaged]
  )

  /** Soft-delete one staged recipient (Phase 4b team-detail Remove). */
  const removeRecipientRow = useCallback(
    async (recipientId: string): Promise<string | null> => {
      const token = getAccessToken(store.getState().auth)
      const res = await removeRecipient(recipientId, token)
      if (cancelledRef.current) return null
      if (res.error) return res.error
      await fetchStaged()
      return null
    },
    [fetchStaged]
  )

  /** Search candidate contacts for the team-detail Add picker. */
  const searchCandidates = useCallback(
    async (
      accountId: string,
      searchTerm: string | null
    ): Promise<TeamCandidateContact[]> => {
      if (!campaignId) return []
      const token = getAccessToken(store.getState().auth)
      const res = await searchTeamCandidates(campaignId, accountId, searchTerm, token)
      if (cancelledRef.current) return []
      if (res.error && process.env.NODE_ENV === 'development') console.warn(res.error)
      return res.data
    },
    [campaignId]
  )

  return {
    // sources
    savedLists,
    listsLoading,
    options,
    optionsLoading,
    // session-only resolve buffer
    resolved,
    resolving,
    resolve,
    clearResolved,
    loadListDefinition,
    // persisted staged grid
    staged,
    stagedLoading,
    stagedTotal,
    refetchStaged: fetchStaged,
    // mutations
    stage,
    staging,
    savePerRecipientOverride,
    // Phase 4b — team-mode mutations
    savePerTeamOverride,
    addRecipientToTeam,
    removeRecipientRow,
    searchCandidates
  }
}
